/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import shortid from 'shortid';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import EventIcon from '@material-ui/icons/Event';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import PrintIcon from '@material-ui/icons/Print';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import LoadingPageText from '../../../common/Loading/LoadingPageText';
import ClinicianAssignedVisits from './ClinicianAssignedVisits';
import useTimeline from '../../timeline/useTimeline';
import { useFetchClinicianDetails } from '../../../../actions/areas/planner';
import { DaysOfWeek } from '../../../../enums/daysOfWeek';
import { downloadPlan } from '../../../../actions/areas/dailyPlanning/downloadPlan';
import globals from '../../../../globals/communityGlobals';
import { autoAllocationStatuses } from '../../../../settings/constants';
import {
  parseShortDateTime,
  startEndTimeDisplay,
  startEndTimeGridDisplay,
  formatHours,
  parseDateUtc,
} from '../../../../utils/dates';
import { autoAllocate, autoUnallocate } from '../../../../actions/areas/dailyPlanning';
import _ from '../../../../utils/lodash';
import { AutoAllocationMode } from '../../../../enums/autoAllocationMode';

const locale = globals.getArea('dailyPlanning');

// eslint-disable-next-line react/prop-types
export default function ClinicianDetails({ isMileageCapEnabled, period, autoAllocateCallback }) {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    forceRefresh: false,
    tab: 0,
    printButtonDisabled: false,
  });
  const {
    activeClinicianId: clinicianId,
    activeClinician,
    setFlagAndClinician,
    getAccessToken,
    setAlertMessage,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    resetContext,
  } = useTimeline();
  const isNightMode = useSelector(store => store.areas.dailyPlanning.scheduler.isNightMode);
  const selectedDate = useSelector(store => store.areas.dailyPlanning.selectedDate);
  const orgUnitId = useSelector(store => store.areas.dailyPlanning.filters.selectedTeam);

  const autoAllocateInProgress = useSelector(store => store.areas.dailyPlanning.autoAllocate.autoAllocateInProgress);
  const autoAllocateByUnit = _.find(
    autoAllocateInProgress,
    item => item.orgUnitId === orgUnitId && item.date === selectedDate,
  );
  const autoAllocateStatus = autoAllocateByUnit ? autoAllocateByUnit.status : 'Idle';
  const isUnallocating = autoAllocateByUnit ? autoAllocateByUnit.isUnAllocating : false;

  if (!clinicianId) return null;

  const { data: clinicianData, isDataLoading, error } = useFetchClinicianDetails(clinicianId, state.forceRefresh);

  useEffect(() => {
    stopLiveTrackingInterval();
    return () => startLiveTrackingInterval();
  }, []);

  useEffect(() => {
    if (error) setAlertMessage({ message: error });
  }, [isDataLoading]);

  const printReport = () => {
    setState({ ...state, printButtonDisabled: true });
    setAlertMessage({ message: locale.getMsg('preparingVisitPlanReport') });
    dispatch(
      downloadPlan(clinicianId, selectedDate, getAccessToken, clinicianData.clinicianDetails.clinicianFullName),
    ).then(() => setState({ ...state, printButtonDisabled: false }));
  };

  const clinicianContactInfomation = () => {
    return (
      <div className="clinician-info-box" id="contact-information">
        <h6>Contact Information</h6>
        <List className="list-underline">
          <ListItem key="csp-phone-item" id="phone">
            <ListItemText primary="Phone" />
            <span className="divider" />
            <ListItemText
              secondary={(clinicianData !== null && clinicianData.cliicianContact.workTelephoneDisplay) || '-'}
            />
          </ListItem>
          <ListItem key="csp-email-item" id="email">
            <ListItemText primary="Email" />
            <span className="divider" />
            <ListItemText secondary={(clinicianData !== null && clinicianData.cliicianContact.email1) || '-'} />
          </ListItem>
        </List>
      </div>
    );
  };

  const clinicianHoursSummary = () => {
    const totalHrs = activeClinician.clinician.totalHours + activeClinician.clinician.availableHours;
    return (
      <div className="clinician-info-box" id="hours-summary">
        <h6>Hours Summary</h6>
        <List className="list-underline">
          <ListItem key="csp-totalh-item" id="total-hours">
            <ListItemText primary="Total Hours" />
            <span className="divider" />
            <ListItemText secondary={formatHours(totalHrs)} />
          </ListItem>
          <ListItem key="csp-assignedt-item" id="assigned-time">
            <ListItemText primary="Assigned time" />
            <span className="divider" />
            <ListItemText secondary={formatHours(activeClinician.clinician.assignedVisitsTimeWithDrivingTime)} />
          </ListItem>
          <ListItem key="csp-availableh-item" id="available-hours">
            <ListItemText primary="Available hours" />
            <span className="divider" />
            <ListItemText secondary={formatHours(activeClinician.clinician.availableHours)} />
          </ListItem>
          <ListItem key="csp-npa-item" id="non-patient-acts">
            <ListItemText primary="Non-Patients Activities" />
            <span className="divider" />
            <ListItemText secondary={formatHours(activeClinician.clinician.nonPatientActivitiesTime)} />
          </ListItem>
        </List>
      </div>
    );
  };

  const fillBlankAvailabilityGridItems = (numOfAvailabilitiesOnDay, maxAvailabilitiesInDay) => {
    const items = [];
    for (let i = 0; i < maxAvailabilitiesInDay - numOfAvailabilitiesOnDay; i += 1) {
      items.push(<Grid className="rostered-time-grey" item key={shortid.generate()} />);
    }
    return items;
  };

  const rosteredWorkingHours = () => {
    const daysOfWeekArray = Object.values(DaysOfWeek);
    const maxAvailabilitiesInDay = clinicianData.weeklyAvailabilities.reduce(
      (max, curr) => Math.max(max, curr.value.length),
      Number.MIN_VALUE,
    );
    return (
      <div className="clinician-info-box" id="work-hours">
        <h6>Rostered working hours</h6>
        <div className="rostered-table">
          <Grid container spacing={1}>
            {clinicianData.weeklyAvailabilities.map((day, index) => (
              <Grid item xs className="rostered-table-item" key={shortid.generate()}>
                <span className="day">{daysOfWeekArray[index]}</span>
                <div className="rostered-time">
                  {day.value.map(availability => (
                    <Grid className="rostered-time-green" item key={shortid.generate()}>
                      {startEndTimeGridDisplay(availability.startDate, availability.endDate)}
                    </Grid>
                  ))}
                  {fillBlankAvailabilityGridItems(day.value.length, maxAvailabilitiesInDay)}
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    );
  };

  const clinicianSkillsRender = skillDetails => {
    return (
      <div className="clinician-info-box skills">
        <List className="list-underline">
          <ListItem key={skillDetails.skill.id}>
            <ListItemText primary={skillDetails.skill.name} />
            <span className="divider" />
            <ListItemText
              secondary={`${parseShortDateTime(skillDetails.validFrom)} - ${parseShortDateTime(skillDetails.validTo)}`}
            />
          </ListItem>
        </List>
      </div>
    );
  };

  const shouldDisableAutoAllocateUnAllocate = () => {
    if (period === null) return true;
    if (parseDateUtc(period.startDate).isSameOrAfter(parseDateUtc(globals.now))) {
      return false;
    }
    return !parseDateUtc(globals.now).isBetween(parseDateUtc(period.startDate), parseDateUtc(period.endDate));
  };

  const onAutoAllocate = () => {
    dispatch(
      autoAllocate({
        date: selectedDate,
        orgUnitId,
        isNightMode,
        createLog: true,
        clinicianIds: [clinicianId],
        autoAllocationModes: [AutoAllocationMode.general, AutoAllocationMode.phoneCalls],
        groupsToExclude: [],
      }),
    )
      .then(autoAllocateCallback)
      .catch(autoAllocateCallback);
    resetContext();
  };

  const onAutoUnallocate = () => {
    dispatch(autoUnallocate({ date: selectedDate, orgUnitId, isNightMode, clinicianIds: [clinicianId] }))
      .then(autoAllocateCallback)
      .catch(autoAllocateCallback);
    resetContext();
  };

  const clinicianHeader = () => {
    const activeDeactiveBtnRender = () => {
      return (
        <List component="nav" style={{ paddingTop: '0px', paddingBottom: '6px' }}>
          <ListItem
            className="header-action-button auto"
            key={'auto-allocate-btn'}
            button
            disabled={
              autoAllocateStatus !== autoAllocationStatuses.IDLE ||
              isUnallocating ||
              shouldDisableAutoAllocateUnAllocate()
            }
            aria-label="Autoallocate"
            onClick={onAutoAllocate}
          >
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText primary={'Auto Allocate'} />
          </ListItem>
          <ListItem
            className="header-action-button"
            key={'clear-allocation-btn'}
            button
            disabled={
              autoAllocateStatus !== autoAllocationStatuses.IDLE ||
              isUnallocating ||
              shouldDisableAutoAllocateUnAllocate()
            }
            aria-label="Unallocate"
            onClick={onAutoUnallocate}
          >
            <ListItemIcon>
              <EventBusyIcon />
            </ListItemIcon>
            <ListItemText primary={'Clear Allocation'} />
          </ListItem>
          {/* <ListItem className="header-action-button" key={'retro-visit-btn'} button>
            <ListItemIcon>
              <AddCircleIcon />
            </ListItemIcon>
            <ListItemText primary={'Add Retro Visit'} />
          </ListItem> */}
          <ListItem
            className="header-action-button"
            key={'print-report-btn'}
            button
            disabled={state.printButtonDisabled}
            onClick={() => printReport()}
          >
            <ListItemIcon>
              <PrintIcon />
            </ListItemIcon>
            <ListItemText primary={'Print plan'} />
          </ListItem>
        </List>
      );
    };
    return (
      <div className="sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">Clinician details</Typography>
            </Box>
            <Box>
              <IconButton
                onClick={() =>
                  setFlagAndClinician('showClinicianDetails', false, 0, 0, [
                    'showClinicianTreatments',
                    'enableMultipleSelection',
                  ])
                }
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
        <div className="sidePanel-header-wrapper">
          <div className="sidePanel-header-left-info-wrap">
            <div className="sidePanel-header-title">
              <Typography variant="h2" gutterBottom>
                {activeClinician.clinician.name}
              </Typography>
              <Typography variant="h3">{activeClinician.clinician.band}</Typography>
              {activeClinician.clinician.hasValidBasePostCode && (
                <Typography variant="h3">{activeClinician.clinician.basePostCode}</Typography>
              )}
            </div>
            <div className="sidePanel-header-info">
              <Box className="assignment-desc-info" display="flex" flexDirection="column">
                <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                  <span className="icon-extend icon-multiple-times" />
                  <span className="info-text">
                    {activeClinician.clinician.availabilities.map(
                      availability =>
                        `${startEndTimeDisplay(availability.actual.startDate, availability.actual.endDate)}`,
                    )}
                  </span>
                </Box>

                {isMileageCapEnabled && (
                  <Box className="header-icon-wrapper" display="flex" flexDirection="row">
                    <span className="icon-extend icon-radius icon-small p-x-4" />
                    <span className="info-text">{activeClinician.clinician.mileageCap || '-'}</span>
                  </Box>
                )}
              </Box>
            </div>
          </div>
          <div className="sidePanel-header-action">{activeDeactiveBtnRender()}</div>
        </div>
      </div>
    );
  };

  const clinicianDetailsRender = () => {
    return (
      <React.Fragment>
        <div className="sidePanel-content hr">
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <Tabs
                className="custom-tabs"
                value={state.tab}
                onChange={(event, value) => setState({ tab: value })}
                centered
              >
                <Tab label="Overview" />
                <Tab label="Skills" />
              </Tabs>
            </Grid>

            <Grid item xs={12}>
              {state.tab === 0 && clinicianData !== null && (
                <Box>
                  {clinicianContactInfomation()}
                  {clinicianHoursSummary()}
                  {rosteredWorkingHours()}
                  <ClinicianAssignedVisits clinician={activeClinician.clinician} />
                </Box>
              )}
              {state.tab === 1 && clinicianData !== null && (
                <Box>{clinicianData.clinicianDetails.clinicianSkills.map(clinicianSkillsRender)}</Box>
              )}
            </Grid>
          </Grid>
        </div>
      </React.Fragment>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open
        onOpen={() => {}}
        onClose={() => {}}
        ModalProps={{
          onBackdropClick: () => setFlagAndClinician('showClinicianDetails', false, 0, 0, ['showClinicianTreatments']),
        }}
      >
        <div className="sidePanel-wrapper" style={{ width: '635px' }} id="clinician-side-panel">
          {clinicianHeader()}
          {isDataLoading ? (
            <LoadingPageText showHeader={false}>Wait a moment while we gather clinician info...</LoadingPageText>
          ) : (
            clinicianDetailsRender()
          )}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
